import * as LabelPrimitive from '@radix-ui/react-label';
import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

const dropdownVariants = cva(
  'flex w-full min-w-[200px] rounded-2xl border-2 border-solid bg-magical-moonlight-white transition-all duration-200 cursor-pointer select-none items-center focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-magical-moonlight-s',
  {
    variants: {
      variant: {
        default:
          'border-magical-moonlight-d1 text-magical-moonlight-dark focus-visible:border-bhanzu-orange focus-visible:ring-2 focus-visible:ring-bhanzu-orange/20',
        error:
          'border-fusion-red text-magical-moonlight-dark focus-visible:border-fusion-red focus-visible:ring-2 focus-visible:ring-fusion-red/20',
      },
      size: {
        // Desktop size (default)
        default: 'h-12 text-sm',
        // Tablet size
        md: 'h-11 text-sm',
        // Mobile size
        sm: 'h-10 text-xs',
        // Large size
        lg: 'h-14 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const labelVariants = cva('block text-sm font-medium mb-2', {
  variants: {
    variant: {
      default: 'text-magical-moonlight-dark',
      error: 'text-fusion-red',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const optionsVariants = cva(
  'px-2 cursor-pointer transition-colors duration-150 mx-2 my-1 hover:bg-magical-moonlight-white/50 rounded-2xl',
  {
    variants: {
      size: {
        sm: 'py-1.5 text-xs',
        md: 'py-2 text-sm',
        default: 'py-3 text-sm',
        lg: 'py-3 text-sm',
      },
      selected: {
        true: 'bg-bhanzu-orange text-magical-moonlight-white hover:bg-bhanzu-orange rounded-2xl',
        false: 'text-magical-moonlight-dark',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed hover:bg-transparent text-magical-moonlight-d1',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      selected: false,
      disabled: false,
    },
  }
);

const chevronVariants = cva(
  'text-bhanzu-orange transition-transform duration-200',
  {
    variants: {
      size: {
        sm: 'h-3 w-3',
        md: 'h-3.5 w-3.5',
        default: 'h-4 w-4',
        lg: 'h-4 w-4',
      },
      open: {
        true: 'rotate-180',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      open: false,
    },
  }
);

interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownProps {
  variant?: 'default' | 'error';
  size?: 'sm' | 'default' | 'md' | 'lg';
  label?: string;
  labelClassName?: string;
  helperText?: string;
  errorText?: string;
  placeholder?: string;
  options: DropdownOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onOptionSelect?: (option: DropdownOption) => void;
  disabled?: boolean;
  className?: string;
  chevronClassName?: string;
  id?: string;
}

const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      className,
      variant,
      size,
      label,
      labelClassName,
      helperText,
      errorText,
      placeholder = 'Select an option',
      options,
      value,
      defaultValue,
      onValueChange,
      onOptionSelect,
      disabled = false,
      id,
      chevronClassName,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedValue, setSelectedValue] = React.useState(
      value ?? defaultValue ?? ''
    );
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const generatedId = React.useId();
    const dropdownId = id ?? generatedId;

    const selectedOption = options.find(
      option => option.value === selectedValue
    );

    const handleToggle = () => {
      if (disabled) return;
      setIsOpen(!isOpen);
    };

    const handleOptionSelect = (option: DropdownOption) => {
      if (option.disabled) return;

      setSelectedValue(option.value);
      setIsOpen(false);

      onValueChange?.(option.value);
      onOptionSelect?.(option);
    };

    const handleClickOutside = React.useCallback((event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }, []);

    React.useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handleClickOutside]);

    React.useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value);
      }
    }, [value]);

    return (
      <div className="relative w-full" ref={dropdownRef}>
        {label ? (
          <LabelPrimitive.Root
            htmlFor={dropdownId}
            className={cn(
              labelVariants({
                variant: errorText ? 'error' : variant,
              }),
              labelClassName
            )}
          >
            {label}
          </LabelPrimitive.Root>
        ) : null}
        <div className="relative">
          <div
            ref={ref}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${dropdownId}-listbox`}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={cn(
              dropdownVariants({
                variant: errorText ? 'error' : variant,
                size,
                className,
              }),
              // Active state when dropdown is open
              isOpen && 'border-magical-moonlight-dark'
            )}
            onClick={handleToggle}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
            {...props}
          >
            <span
              className={cn(
                'flex-1 px-4 py-3 text-left',
                selectedOption
                  ? 'text-magical-moonlight-dark'
                  : 'text-magical-moonlight-d1'
              )}
            >
              {selectedOption?.label ?? placeholder}
            </span>
            <div className="bg-bhanzu-orange-light-cardbg rounded-lg p-2 mr-2.5 flex items-center justify-center">
              <ChevronDown
                className={chevronVariants({
                  size,
                  open: isOpen,
                  className: chevronClassName,
                })}
              />
            </div>
          </div>
        </div>
        {/* Dropdown menu */}
        {isOpen && options.length > 0 ? (
          <div
            id={`${dropdownId}-listbox`}
            role="listbox"
            className="absolute top-full left-0 right-0 z-(--z-popover) mt-1 bg-magical-moonlight-s border-2 border-magical-moonlight-d1 rounded-2xl shadow-lg max-h-60 overflow-y-auto"
          >
            {options.map((option, index) => (
              <div
                key={option.value}
                role="option"
                aria-selected={selectedValue === option.value}
                tabIndex={0}
                className={cn(
                  optionsVariants({
                    size,
                    selected: selectedValue === option.value,
                    disabled: option.disabled,
                  }),
                  index === 0 && 'rounded-t-2xl',
                  index === options.length - 1 && 'rounded-b-2xl'
                )}
                onClick={() => handleOptionSelect(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOptionSelect(option);
                  }
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        ) : null}
        {/* Helper text or error text */}
        {(Boolean(helperText) || Boolean(errorText)) && (
          <div className="mt-1 px-1">
            {Boolean(errorText) && (
              <p className="text-xs text-fusion-red font-medium">{errorText}</p>
            )}
            {!errorText && Boolean(helperText) && (
              <p className="text-xs text-bhanzu-orange font-medium">
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
export { dropdownVariants, labelVariants, optionsVariants, chevronVariants };
export type { DropdownOption, DropdownProps };
